import { Box, Flex, Text } from "@chakra-ui/react"
import React, {
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { IntRange } from "./types"
import { useClickAway } from "use-click-away"
import useEase from "../hooks/useEase"
import TileTooltip from "../tooltip"
import { ActiveTopic } from "../../../recoil/atoms/lessons"

const DARK = "#181616"

export function fillColor(progress: number) {
  return `hsl(${30 + progress * 100}, 65%, 60%)`
}

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  var angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  }
}

function describeArc(
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number
) {
  var start = polarToCartesian(x, y, radius, endAngle)
  var end = polarToCartesian(x, y, radius, startAngle)

  var largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"

  var d = [
    "M",
    start.x,
    start.y,
    "A",
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y
  ].join(" ")

  return d
}

export type Progress = IntRange<0, 101>

export type CenterPos = { x: number; y: number }

type Props = {
  progress: { start: Progress; new: Progress }
  name: string
  id: string
  index: number
  drillDown?: (activeTopic: ActiveTopic) => void
  isBlack?: boolean
  menuItemsDeselected?: boolean
  showTooltip?: boolean
}

function Tile({
  progress,
  name,
  id,
  index,
  isBlack = false,
  drillDown = () => null,
  menuItemsDeselected = false,
  showTooltip = false
}: Props) {
  const endAngle = useMemo(() => 360 * (progress.new / 100), [progress])
  const startAngle = useMemo(() => 360 * (progress.start / 100), [progress])

  const { animationProgress } = useEase({
    start: startAngle,
    end: endAngle,
    fillMode: "persist",
    ease: "easeInOutQuint",
    speed: 1
  })

  const [isMounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const currentRotation = animationProgress.ease
  const fullRotation = 359
  const rotation =
    currentRotation < fullRotation ? currentRotation : fullRotation

  const currentPercentage = `${(
    (animationProgress.ease / 360) *
    100
  ).toFixed()}%`

  const [showGoButton, setShowGoButton] = useState(false)

  useEffect(() => {
    if (menuItemsDeselected) setShowGoButton(false)
  }, [menuItemsDeselected])

  const clickRef = React.useRef<HTMLDivElement | null>(null)

  useClickAway(clickRef, () => {
    setShowGoButton(false)
  })

  const drillDownHandler = () => {
    const { left, top, width, height } =
      clickRef.current.getBoundingClientRect()
    drillDown({
      id,
      centerPos: { x: left + width / 2, y: top + height / 2 },
      color: fillColor(animationProgress.ease / 360),
      name
    })
  }

  function clickHandler() {
    setShowGoButton(true)
    setTimeout(() => drillDownHandler(), 200)
  }

  return (
    <Flex
      position="relative"
      flexDir="column"
      left={`calc(clamp(72px, 180px, 18vw) * sin(${index * 60}deg))`}
      justifyContent="center"
    >
      <Flex
        position="relative"
        alignItems="center"
        justifyContent="center"
        ref={clickRef}
        transition="opacity .45s ease-out"
        transitionDelay={`${index * 100}ms`}
        opacity={isMounted ? 1 : 0}
      >
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          width="100px"
          height="100px"
          overflow="visible"
        >
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke={isBlack ? "#00000080" : DARK}
            strokeWidth="8.5"
          />
          <path
            fill="transparent"
            stroke={
              isBlack ? "#000000" : fillColor(animationProgress.ease / 360)
            }
            strokeWidth="8.5px"
            strokeLinecap="round"
            d={describeArc(50, 50, 42, 0, rotation)}
          />
          <circle
            cx="50"
            cy="50"
            r="29"
            fill={isBlack ? "#000000" : fillColor(animationProgress.ease / 360)}
            cursor="pointer"
            onClick={clickHandler}
          />
        </svg>
        <Text
          position="absolute"
          color={isBlack ? "#F5FFF5" : "#201D1E"}
          fontSize="20px"
          fontFamily="Rubik"
          cursor="pointer"
          onClick={clickHandler}
          transition="all .25s ease-in-out"
          transform={showGoButton ? "scale(0.25)" : "unset"}
          opacity={showGoButton ? 0 : 1}
        >
          {currentPercentage}
        </Text>
        <Text
          position="absolute"
          color={isBlack ? "#F5FFF5" : "#201D1E"}
          fontSize="24px"
          fontFamily="Poppins"
          cursor="pointer"
          onClick={clickHandler}
          transition="all .25s ease-in-out"
          transform={!showGoButton ? "scale(1.25)" : "scale(1)"}
          opacity={!showGoButton ? 0 : 1}
        >
          Go!
        </Text>
        {showTooltip && <TileTooltip />}
      </Flex>
      <Text
        fontSize="18px"
        fontFamily="Poppins"
        color="#F5FFF5"
        textAlign="center"
        cursor="pointer"
        transition="opacity .45s ease-out"
        opacity={isMounted ? 1 : 0}
        transitionDelay={`${index * 100}ms`}
        maxWidth="180px"
        wordBreak="normal"
        onClick={clickHandler}
      >
        {name}
      </Text>
    </Flex>
  )
}

export default Tile
